'use client';

import { useEffect, useId, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { Button, Skeleton, Typography } from 'lighthouse';

import { SelectCityModal } from '@/widgets/AppBar/_components/SelectCityModal';
import { LocationsMapProps } from '@/widgets/LocationsMap/models';

import { useSelectCity } from '@/hooks/user/useSelectCity';

import { getAgentLocation } from '@/utilities/getAgentLocation';

import { useCityStore } from '@/stores/city/useCityStore';

import { defaultCitiesLocations, TEHRAN_CITY_ID } from '@/constants/common';
import { vendorStores } from '@/constants/routes';

import { HOME_STORE_FULFILLMENT_OPTIONS, HOME_STORES_MAX_SIZE, HOME_STORES_MIN_SIZE } from '../../constants';
import { NearestStoresList } from '../NearestStoresList';
import { getDistanceValue, getRateValue } from '../StoreCard/utils';
import { StoreFilterChips } from '../StoreFilterChips';
import { NearbyStoresLocation, NearbyStoresResult, NearestStoresClientProps } from './models';

import { getNearestStoresService } from '@/client/services/vendor/stores/methods';
import { GetStoresServiceQueryParamType, StoreFulfillmentType, StoreSortBy, StoreType } from '@/server/services/vendor/stores/models';
import { ArrowDown2, ArrowLeft, Gps, Location, Map1, Shop } from 'iconsax-react';

const DynamicLocationsMap = dynamic<LocationsMapProps>(() => import('@/widgets/LocationsMap').then((mod) => mod.LocationsMap), {
    ssr: false,
    loading: () => <Skeleton variant='rounded' className='h-80 w-full md:h-96' />
});

export const NearestStoresClient = ({
    pageSize,
    title,
    subTitle,
    onlyList,
    servingCategoryIds,
    geoFilterType = 'location'
}: NearestStoresClientProps) => {
    const { userCity } = useSelectCity();
    const cityId = useCityStore((state) => state.cityId);
    const [mounted, setMounted] = useState(false);
    const [result, setResult] = useState<NearbyStoresResult | null>(null);
    const [userLocation, setUserLocation] = useState<NearbyStoresLocation | null>(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState('');
    const [selectCityOpen, setSelectCityOpen] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    const [activeStoreId, setActiveStoreId] = useState<string>();
    const [fulfillmentType, setFulfillmentType] = useState<StoreFulfillmentType | ''>('');
    const [retry, setRetry] = useState(0);
    const locationRequest = useRef(0);
    const mapRef = useRef<HTMLDivElement>(null);
    const headingId = useId();
    const mapId = useId();
    const selectedCityId = mounted ? userCity?.id || cityId : TEHRAN_CITY_ID;
    const cityName = mounted ? userCity?.name || 'تهران' : 'تهران';
    const cityKey = `${selectedCityId}:${cityName}`;
    const coordinates = geoFilterType === 'location' && userLocation?.cityKey === cityKey ? userLocation : null;
    const showDistance = !!coordinates;
    const requestSize = Math.min(Math.max(pageSize || 0, HOME_STORES_MIN_SIZE), HOME_STORES_MAX_SIZE);
    const sortBy: StoreSortBy = coordinates ? 'nearest' : 'rating';
    const signature = JSON.stringify([selectedCityId, coordinates?.lat, coordinates?.lng, requestSize, servingCategoryIds, fulfillmentType, retry]);
    const busy = !mounted || result?.signature !== signature;
    const stores = !busy && result ? result.stores : [];
    const hasError = !busy && result?.error;
    const hasFilter = !!fulfillmentType;
    const buildQuery = (page: number): GetStoresServiceQueryParamType => ({
        page,
        size: requestSize,
        sortBy,
        ...(coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : { customerCityId: selectedCityId }),
        ...(fulfillmentType ? { fulfillmentType } : {}),
        servingCategoryIds
    });
    const activeStore = stores.find((store) => store.uuid === activeStoreId);
    const mapDefaultLocation: [number, number] = coordinates
        ? [coordinates.lat, coordinates.lng]
        : [
              userCity?.latitude ?? defaultCitiesLocations[selectedCityId]?.[0] ?? defaultCitiesLocations[TEHRAN_CITY_ID][0],
              userCity?.longitude ?? defaultCitiesLocations[selectedCityId]?.[1] ?? defaultCitiesLocations[TEHRAN_CITY_ID][1]
          ];
    const directoryQuery = new URLSearchParams();
    if (coordinates) {
        directoryQuery.set('lat', String(coordinates.lat));
        directoryQuery.set('lng', String(coordinates.lng));
    } else {
        directoryQuery.set('customerCityId', String(selectedCityId));
    }
    directoryQuery.set('sortBy', sortBy);
    if (fulfillmentType) directoryQuery.set('fulfillmentType', fulfillmentType);
    if (servingCategoryIds?.length) directoryQuery.set('servingCategoryIds', servingCategoryIds.join(','));
    const directoryHref = `${vendorStores}?${directoryQuery}`;
    const sectionTitle = coordinates ? title || 'فروشگاه‌های نزدیک شما' : `فروشگاه‌های ${cityName}`;
    const storeLocations = stores
        .filter((store) => Number.isFinite(store.latitude) && Number.isFinite(store.longitude))
        .map((store) => ({
            id: store.uuid,
            lat: store.latitude,
            lng: store.longitude,
            title: store.name,
            address: [store.cityName?.trim(), store.address?.trim()].filter(Boolean).join('، '),
            rate: getRateValue(store.rate),
            distanceKm: showDistance ? getDistanceValue(store.distanceKm) : null,
            ratesNumber: store.ratesNumber || null
        }));

    useEffect(() => {
        setMounted(true);
        return () => {
            locationRequest.current += 1;
        };
    }, []);

    useEffect(() => {
        if (!mounted) return;
        let active = true;
        const handleGetStores = async () => {
            try {
                const response = await getNearestStoresService(buildQuery(0));
                const page = response?.data?.content;
                const content = page?.content as unknown as StoreType[];
                if (!Array.isArray(content)) throw new Error('Invalid stores response');
                if (active) {
                    setResult({
                        signature,
                        stores: content,
                        error: false,
                        total: page.totalElements ?? content.length
                    });
                }
            } catch {
                if (active) setResult({ signature, stores: [], error: true, total: 0 });
            }
        };
        void handleGetStores();
        return () => {
            active = false;
        };
    }, [mounted, signature]);

    useEffect(() => {
        locationRequest.current += 1;
        setLocating(false);
        setLocationError('');
        setUserLocation(null);
        setActiveStoreId(undefined);
    }, [cityKey]);

    useEffect(() => {
        if (!mapOpen || !activeStoreId) return;
        mapRef.current?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
            block: 'nearest'
        });
    }, [mapOpen, activeStoreId]);

    const handleLocate = async () => {
        const request = ++locationRequest.current;
        const requestedCity = cityKey;
        setLocating(true);
        setLocationError('');
        try {
            const location = await getAgentLocation();
            if (request !== locationRequest.current) return;
            setUserLocation({ ...location, cityKey: requestedCity });
            setActiveStoreId(undefined);
        } catch {
            if (request === locationRequest.current) {
                setLocationError('موقعیت دریافت نشد. دسترسی مکان مرورگر رو بررسی کن یا شهرت رو انتخاب کن.');
            }
        } finally {
            if (request === locationRequest.current) setLocating(false);
        }
    };

    const handleShowOnMap = (store: StoreType) => {
        setActiveStoreId(store.uuid);
        setMapOpen(true);
    };

    return (
        <div aria-labelledby={headingId}>
            <div className='mb-4 flex items-start justify-between gap-3 md:mb-5'>
                <div className='min-w-0'>
                    <Typography
                        id={headingId}
                        variant='h5'
                        component='h2'
                        color='blackHighEmphasis'
                        className='!m-0 font-bold leading-relaxed md:text-3xl'>
                        {sectionTitle}
                    </Typography>
                    <div className='mt-1 flex flex-wrap items-center gap-x-2 gap-y-1'>
                        <button
                            type='button'
                            onClick={() => setSelectCityOpen(true)}
                            aria-label={`تغییر شهر، شهر فعلی ${cityName}`}
                            aria-haspopup='dialog'
                            aria-expanded={selectCityOpen}
                            className='-mr-2 inline-flex min-h-10 min-w-0 cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-2
                                text-secondary transition-colors hover:bg-primary/5 focus-visible:outline focus-visible:outline-2
                                focus-visible:outline-offset-2 focus-visible:outline-secondary motion-reduce:transition-none'>
                            <Location size={16} color='currentColor' variant='Bold' className='shrink-0' aria-hidden />
                            <Typography variant='body2' component='span' className='truncate font-bold text-current'>
                                {cityName}
                            </Typography>
                            <ArrowDown2 size={12} color='currentColor' className='shrink-0' aria-hidden />
                        </button>
                        <Typography variant='body2' component='span' color='blackMediumEmphasis' className='leading-7'>
                            {busy || hasError || !result?.total
                                ? subTitle || 'فروشگاه‌ها رو ببین و با خیال راحت انتخاب کن.'
                                : `· ${result.total.toLocaleString('fa-IR')} فروشگاه فعال`}
                        </Typography>
                    </div>
                </div>
                <Button
                    component={Link}
                    href={directoryHref}
                    variant='text'
                    endIcon={<ArrowLeft size={16} color='currentColor' aria-hidden />}
                    className='hidden min-h-11 shrink-0 whitespace-nowrap rounded-lg px-2 text-secondary focus-visible:outline focus-visible:outline-2
                        focus-visible:outline-offset-2 md:inline-flex'>
                    همهٔ فروشگاه‌ها
                </Button>
            </div>

            <div className='mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                <StoreFilterChips
                    label='نوع خدمت'
                    options={HOME_STORE_FULFILLMENT_OPTIONS}
                    value={fulfillmentType}
                    onChange={(value) => setFulfillmentType(value as StoreFulfillmentType | '')}
                />
                {(geoFilterType === 'location' || (!onlyList && storeLocations.length > 0)) && (
                    <div className='flex shrink-0 items-center gap-1'>
                        {geoFilterType === 'location' && (
                            <Button
                                variant='text'
                                size='small'
                                onClick={handleLocate}
                                disabled={locating}
                                aria-busy={locating}
                                startIcon={<Gps size={16} color='currentColor' aria-hidden />}
                                className='min-h-10 rounded-xl px-3 text-secondary'>
                                {locating ? 'در حال دریافت موقعیت…' : coordinates ? 'به‌روزرسانی موقعیت' : 'نزدیک‌ترین‌ها به من'}
                            </Button>
                        )}
                        {!onlyList && storeLocations.length > 0 && (
                            <Button
                                variant='text'
                                size='small'
                                aria-expanded={mapOpen}
                                aria-controls={mapId}
                                onClick={() => setMapOpen((value) => !value)}
                                startIcon={<Map1 size={16} color='currentColor' aria-hidden />}
                                className='min-h-10 rounded-xl px-3 text-secondary'>
                                {mapOpen ? 'بستن نقشه' : 'نقشه'}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {locationError && (
                <Typography role='alert' variant='body2' component='p' color='error' className='mb-4 leading-7'>
                    {locationError}
                </Typography>
            )}
            {coordinates && (
                <Typography variant='caption' component='p' color='blackMediumEmphasis' className='mb-3 mt-0 leading-6'>
                    فاصله‌ها بر اساس موقعیت فعلی تو نمایش داده می‌شن و نزدیک‌ترین‌ها اول نمایش داده می‌شن.
                </Typography>
            )}
            <div aria-busy={busy}>
                <span role='status' className='sr-only'>
                    {busy
                        ? 'در حال دریافت فروشگاه‌ها'
                        : hasError
                          ? 'خطا در دریافت فروشگاه‌ها'
                          : `${(result?.total ?? stores.length).toLocaleString('fa-IR')} فروشگاه پیدا شد`}
                </span>
                {busy ? (
                    <div aria-hidden className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
                        {Array.from({ length: 3 }, (_, index) => (
                            <div
                                key={index}
                                className={`${index > 1 ? 'hidden lg:block' : index ? 'hidden md:block' : ''} overflow-hidden rounded-2xl border
                                    border-solid border-N200 bg-white`}>
                                <Skeleton variant='rectangular' height={112} />
                                <div className='p-4'>
                                    <Skeleton width='60%' height={28} />
                                    <Skeleton width='90%' height={20} className='mt-2' />
                                    <Skeleton height={40} className='mt-4' />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : hasError || !stores.length ? (
                    <div
                        className='flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-solid border-N200 bg-white px-5
                            py-10 text-center'>
                        <Shop size={32} color='currentColor' className='text-blackMediumEmphasis' aria-hidden />
                        <Typography variant='subtitle1' component='p' color='blackHighEmphasis' className='!m-0 font-bold'>
                            {hasError
                                ? 'فروشگاه‌ها دریافت نشدن'
                                : hasFilter
                                  ? 'با این نوع خدمت فروشگاهی پیدا نشد'
                                  : 'فعلاً فروشگاهی در این محدوده پیدا نشد'}
                        </Typography>
                        <Typography variant='body2' component='p' color='blackMediumEmphasis' className='!m-0 leading-7'>
                            {hasError
                                ? 'اتصال اینترنت رو بررسی کن و دوباره تلاش کن.'
                                : hasFilter
                                  ? 'فیلتر رو بردار تا همهٔ فروشگاه‌های این شهر رو ببینی.'
                                  : 'می‌تونی شهر دیگه‌ای رو انتخاب کنی یا همهٔ فروشگاه‌ها رو ببینی.'}
                        </Typography>
                        <Button
                            variant='outlined'
                            onClick={() => {
                                if (hasError) setRetry((value) => value + 1);
                                else if (hasFilter) setFulfillmentType('');
                                else setSelectCityOpen(true);
                            }}
                            className='mt-2 min-h-11 border-N300 text-secondary'>
                            {hasError ? 'تلاش دوباره' : hasFilter ? 'حذف فیلتر' : 'تغییر شهر'}
                        </Button>
                    </div>
                ) : (
                    <NearestStoresList
                        stores={stores}
                        showDistance={showDistance}
                        activeStoreId={activeStoreId}
                        onShowOnMap={onlyList ? undefined : handleShowOnMap}
                        seeAll={{ href: directoryHref, total: result?.total ?? stores.length, cityName }}
                    />
                )}
            </div>
            {!onlyList && mapOpen && storeLocations.length > 0 && (
                <div
                    id={mapId}
                    ref={mapRef}
                    role='region'
                    aria-label='نقشهٔ فروشگاه‌ها'
                    className='mt-5 h-80 overflow-hidden rounded-2xl border border-solid border-N200 bg-white md:h-96'>
                    <DynamicLocationsMap
                        key={signature}
                        zoomToUserOnInit={false}
                        locations={storeLocations}
                        focusLocation={activeStore ? { lat: activeStore.latitude, lng: activeStore.longitude } : undefined}
                        activeLocationId={activeStoreId}
                        onChange={(location) => {
                            const selected = storeLocations.find((store) => store.lat === location.lat && store.lng === location.lng);
                            if (selected) setActiveStoreId(selected.id);
                        }}
                        initialZoom={11}
                        className='h-full !border-0 !shadow-none'
                        defaultLocation={mapDefaultLocation}
                    />
                </div>
            )}
            {selectCityOpen && <SelectCityModal requestLocationOnOpen={false} allowClose onClose={() => setSelectCityOpen(false)} />}
        </div>
    );
};
